import React from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Icon,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Stack,
  alpha,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Collapse,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { bomService } from "../../../services/bom.service";
import { BOMFormData, BOMIngredient } from "../../../types/bom.types";
import { inventoryService } from "../../../services/inventory.service";
import { kitchenService } from "../../../services/kitchen.service";
import { IngredientDialog } from "../../Kitchen/Recipes/components/IngredientDialog";
import { IngredientOption } from "../../Kitchen/Recipes/types";
import { ProcessedItemDialog } from "./components/ProcessedItemDialog";

export const THEME_COLORS = {
  primary: '#0891B2',  // Cyan-600
  primaryHover: '#0E7490', // Cyan-700
  secondary: '#64748B', // Slate-500
  danger: '#DC2626', // Red-600
  dangerHover: '#B91C1C', // Red-700
  success: '#059669', // Emerald-600
  background: {
    main: '#FFFFFF',
    light: '#F8FAFC',
    card: '#FFFFFF',
  },
  border: {
    light: alpha('#0891B2', 0.1),
    main: alpha('#0891B2', 0.2),
  },
  text: {
    primary: '#1E293B', // Slate-800
    secondary: '#64748B', // Slate-500
    light: '#94A3B8', // Slate-400
  }
};

interface ProcessedItemOption {
  _id: string;
  name: string;
  batch_id: string;
  production_date: number;
  expiry_date: number;
  quantity: number;
  remaining_quantity: number;
  unit: string;
  storage_place: string;
  recipe_name: string;
  cost_per_unit: number;
}

interface BatchInfo {
  batches: any[];
  totalCost: number;
  averageCost: number;
  totalQuantity: number;
  lastFetched: number;
}

export const BOMForm: React.FC = (): React.ReactNode => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [ingredientOptions, setIngredientOptions] = React.useState<
    IngredientOption[]
  >([]);
  const [processedItemOptions, setProcessedItemOptions] = React.useState<
    ProcessedItemOption[]
  >([]);
  const [loadingIngredients, setLoadingIngredients] = React.useState(false);
  const [loadingProcessedItems, setLoadingProcessedItems] =
    React.useState(false);
  const [batchDataCache, setBatchDataCache] = React.useState<
    Record<string, BatchInfo>
  >({});
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = React.useState({
    ingredients: true,
    processedItems: true,
    preparationSteps: true,
  });

  const [formData, setFormData] = React.useState<BOMFormData>({
    name: "",
    description: "",
    ingredients: [],
    processed_items: [],
    preparation_steps: [],
    shelf_life_duration: 0,
    is_active: true,
    store_id: "",
  });

  const [itemDialogOpen, setItemDialogOpen] = React.useState(false);
  const [itemDialogType, setItemDialogType] = React.useState<
    "ingredient" | "processed"
  >("ingredient");
  const [editingItemIndex, setEditingItemIndex] = React.useState<number | null>(
    null
  );

  React.useEffect(() => {
    if (id) {
      loadBOM();
    }
    loadIngredients();
    loadProcessedItems();
  }, [id]);

  const loadBOM = async () => {
    try {
      setLoading(true);
      const bom = await bomService.getBOM(id!);
      console.log({ bom });
      setFormData(bom);
    } catch (error) {
      toast.error("Failed to load BOM");
      navigate("/business/menu/bom");
    } finally {
      setLoading(false);
    }
  };

  const loadIngredients = async () => {
    try {
      setLoadingIngredients(true);
      const response = await inventoryService.listIngredients({
        pageIndex: 0,
        pageSize: 100,
        sortField: "created_at",
        sortOrder: "desc",
      });

      const ingredients: IngredientOption[] = response.data.map(
        (item: any) => ({
          id: item.ingredient_id,
          _id: item._id,
          ingredient_id: item.ingredient_id,
          name: item.name,
          unit: item.unit,
          type: "ingredient" as "ingredient" | "supply",
          description: item.description,
          stock_quantity: item.stock_quantity,
        })
      );

      setIngredientOptions(ingredients);
    } catch (error) {
      console.error("Error loading ingredients:", error);
      toast.error("Failed to load ingredients");
    } finally {
      setLoadingIngredients(false);
    }
  };

  const loadProcessedItems = async () => {
    try {
      setLoadingProcessedItems(true);
      const response = await kitchenService.listProcessedItems({
        pageIndex: 0,
        pageSize: 100,
        sortField: "created_at",
        sortOrder: "desc",
      });

      setProcessedItemOptions(response.data);
    } catch (error) {
      console.error("Error loading processed items:", error);
      toast.error("Failed to load processed items");
    } finally {
      setLoadingProcessedItems(false);
    }
  };

  const loadIngredientBatches = async (ingredientId: string) => {
    try {
      const response = await inventoryService.listBatches({
        ingredient_id: ingredientId,
        pageIndex: 0,
        pageSize: 100,
        sortField: "expiry_date",
        sortOrder: "asc",
        include_wasted: false,
      });

      const batches: any[] = response.data.batches;
      const totalCost = batches.reduce(
        (sum, batch) => sum + (batch.total_cost || 0),
        0
      );
      const totalQuantity = batches.reduce(
        (sum, batch) => sum + batch.remaining_quantity,
        0
      );
      const averageCost = totalQuantity > 0 ? totalCost / totalQuantity : 0;

      const batchInfo = {
        batches,
        totalCost,
        averageCost,
        totalQuantity,
        lastFetched: Date.now(),
      };

      setBatchDataCache((prev) => ({
        ...prev,
        [ingredientId]: batchInfo,
      }));

      return batchInfo;
    } catch (error) {
      console.error("Error loading batches:", error);
      toast.error("Failed to load batches");
      return null;
    }
  };

  const getBatchData = React.useCallback(
    async (ingredientId: string) => {
      const cachedData = batchDataCache[ingredientId];
      const now = Date.now();
      if (cachedData && now - cachedData.lastFetched < 5 * 60 * 1000) {
        return cachedData;
      }

      return loadIngredientBatches(ingredientId);
    },
    [batchDataCache]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);
      const submissionData = {
        ...formData,
      };

      if (id) {
        await bomService.updateBOM(id, submissionData);
        toast.success("BOM updated successfully");
      } else {
        await bomService.createBOM(submissionData);
        toast.success("BOM created successfully");
      }
      navigate("/business/menu/bom");
    } catch (error) {
      console.error("Error saving BOM:", error);
      toast.error(`Failed to ${id ? "update" : "create"} BOM`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/business/menu/bom");
  };

  const handleChange = (field: keyof BOMFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddPreparationStep = () => {
    const newStepNumber = formData.preparation_steps?.length || 0;
    setFormData((prev) => ({
      ...prev,
      preparation_steps: [
        ...(prev.preparation_steps || []),
        {
          cooking_time: 0,
          cooking_time_unit: "min",
          instruction: "",
          preparation_time: 0,
          preparation_time_unit: "min",
          step_number: newStepNumber + 1,
          temperature: 0,
          temperature_unit: "C",
        },
      ],
    }));
    // Ensure the section is expanded when adding a new step
    setExpandedSections((prev) => ({
      ...prev,
      preparationSteps: true,
    }));
    // Scroll to the new step after a brief delay to allow for render
    setTimeout(() => {
      const newStepElement = document.querySelector(`[data-step-number="${newStepNumber + 1}"]`);
      if (newStepElement) {
        newStepElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleRemovePreparationStep = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      preparation_steps: (prev.preparation_steps || []).filter(
        (_, i) => i !== index
      ),
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Basic info validation
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.shelf_life_duration)
      newErrors.shelf_life_duration = "Shelf life is required";

    // Validate ingredients and processed items
    const hasIngredients = formData.ingredients.length > 0;
    const hasProcessedItems = (formData.processed_items?.length || 0) > 0;

    if (!hasIngredients && !hasProcessedItems) {
      newErrors.ingredients =
        "At least one ingredient or processed item is required";
    }

    // Validate ingredient details if present
    formData.ingredients.forEach((ing, index) => {
      if (!ing.ingredient_id)
        newErrors[`ingredient_${index}`] = "Select an ingredient";
      if (!ing.quantity)
        newErrors[`quantity_${index}`] = "Quantity is required";
    });

    // Validate processed item details if present
    formData.processed_items?.forEach((item, index) => {
      if (!item.processed_item_id)
        newErrors[`processed_item_${index}`] = "Select a processed item";
      if (!item.consumbtion_quantity)
        newErrors[`processed_quantity_${index}`] = "Quantity is required";
    });

    // Validate preparation steps if any are present
    formData.preparation_steps?.forEach((step, index) => {
      if (!step.instruction)
        newErrors[`instruction_${index}`] = "Instruction is required";
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderSectionHeader = (
    title: string,
    section: keyof typeof expandedSections,
    addAction: () => void,
    count: number,
    icon: string
  ) => (
    <Card
      sx={{
        mb: 0.5,
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
      }}
      onClick={() => toggleSection(section)}
    >
      <CardContent sx={{ p: "12px !important" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flex: 1 }}
          >
            <Icon color="primary">{icon}</Icon>
            <Typography variant="h6" sx={{ fontSize: "1.1rem" }}>
              {title}
            </Typography>
            <Chip
              size="small"
              label={count}
              sx={{
                bgcolor: count === 0 
                  ? alpha(THEME_COLORS.danger, 0.08)
                  : alpha(THEME_COLORS.primary, 0.08),
                color: count === 0 
                  ? THEME_COLORS.danger
                  : THEME_COLORS.primary,
                fontWeight: 500,
                borderRadius: 1,
                border: `1px solid ${count === 0 
                  ? alpha(THEME_COLORS.danger, 0.2)
                  : alpha(THEME_COLORS.primary, 0.2)}`,
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              startIcon={<Icon>add</Icon>}
              onClick={(e) => {
                e.stopPropagation();
                addAction();
              }}
              variant="contained"
              size="small"
            >
              Add {title}
            </Button>
            <Icon>
              {expandedSections[section] ? "expand_less" : "expand_more"}
            </Icon>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderBasicInfo = () => (
    <Box sx={{ mx: "auto", mb: 4 }}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "Montserrat",
          fontWeight: 600,
          color: THEME_COLORS.text.primary,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Icon sx={{ color: THEME_COLORS.primary }}>description</Icon>
        Basic Information
      </Typography>

      <Card 
        sx={{ 
          border: `1px solid ${THEME_COLORS.border.light}`,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="BOM Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                error={!!errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <Icon sx={{ mr: 1, color: THEME_COLORS.primary }}>label</Icon>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLORS.primary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLORS.primary,
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Shelf Life (days)"
                type="number"
                value={formData.shelf_life_duration}
                onChange={(e) =>
                  handleChange("shelf_life_duration", parseInt(e.target.value))
                }
                required
                error={!!errors.shelf_life_duration}
                helperText={errors.shelf_life_duration}
                InputProps={{
                  startAdornment: (
                    <Icon sx={{ mr: 1, color: THEME_COLORS.primary }}>schedule</Icon>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLORS.primary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLORS.primary,
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Icon sx={{ mr: 1, mt: 1, color: THEME_COLORS.primary }}>notes</Icon>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLORS.primary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLORS.primary,
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  const renderPreparationSteps = () => (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "Montserrat",
          fontWeight: 600,
          color: THEME_COLORS.text.primary,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Icon sx={{ color: THEME_COLORS.primary }}>format_list_numbered</Icon>
        Preparation Instructions
      </Typography>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack 
              direction="row" 
              spacing={1} 
              alignItems="center" 
              justifyContent="space-between"
              onClick={() => toggleSection("preparationSteps")}
              sx={{ 
                cursor: 'pointer',
                p: 1.5,
                borderRadius: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: alpha(THEME_COLORS.primary, 0.04),
                  '& .collapse-icon': {
                    color: THEME_COLORS.primary,
                  },
                  '& .section-title': {
                    color: THEME_COLORS.primary,
                  }
                }
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon sx={{ color: THEME_COLORS.primary }}>checklist</Icon>
                <Box
                  className="section-title"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    color: THEME_COLORS.text.primary,
                    fontFamily: "Montserrat",
                    fontWeight: 600,
                    transition: 'color 0.2s ease-in-out',
                  }}
                >
                  Steps
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: alpha(THEME_COLORS.primary, 0.08),
                      color: THEME_COLORS.primary,
                      border: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    {formData.preparation_steps?.length || 0}
                  </Box>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  startIcon={<Icon>add</Icon>}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddPreparationStep();
                  }}
                  variant="contained"
                  size="small"
                  sx={{
                    bgcolor: THEME_COLORS.primary,
                    '&:hover': {
                      bgcolor: THEME_COLORS.primaryHover,
                    },
                  }}
                >
                  Add Step
                </Button>
                <Icon 
                  className="collapse-icon"
                  sx={{ 
                    transition: 'color 0.2s',
                    color: THEME_COLORS.text.secondary,
                  }}
                >
                  {expandedSections.preparationSteps ? "expand_less" : "expand_more"}
                </Icon>
              </Stack>
            </Stack>

            <Collapse in={expandedSections.preparationSteps}>
              <Box sx={{ mt: 2 }}>
                {errors.steps && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.steps}
                  </Alert>
                )}
                {formData.preparation_steps?.map((step, index) => (
                  <Box
                    key={index}
                    data-step-number={step.step_number}
                    sx={{
                      display: 'flex',
                      gap: 3,
                      mb: index < formData.preparation_steps!.length - 1 ? 4 : 0,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '20px',
                        top: '40px',
                        bottom: index < formData.preparation_steps!.length - 1 ? '-32px' : '0',
                        width: '2px',
                        bgcolor: THEME_COLORS.border.light,
                      },
                      // Add animation for new steps
                      animation: step.step_number === formData.preparation_steps?.length ? 
                        'fadeInScale 0.3s ease-out' : 'none',
                      '@keyframes fadeInScale': {
                        '0%': {
                          opacity: 0,
                          transform: 'scale(0.95)',
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'scale(1)',
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: THEME_COLORS.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {step.step_number}
                      </Typography>
                    </Box>
                    <Card
                      sx={{
                        flex: 1,
                        bgcolor: THEME_COLORS.background.light,
                        border: `1px solid ${THEME_COLORS.border.light}`,
                        borderRadius: 3,
                        boxShadow: 'none',
                        p: 3,
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: 'Montserrat',
                            fontWeight: 600,
                            color: THEME_COLORS.text.primary,
                            fontSize: '1.1rem',
                          }}
                        >
                          Step {step.step_number}
                        </Typography>
                        <IconButton
                          onClick={() => handleRemovePreparationStep(index)}
                          size="small"
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: alpha(THEME_COLORS.danger, 0.08),
                            color: THEME_COLORS.danger,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              bgcolor: alpha(THEME_COLORS.danger, 0.12),
                              boxShadow: "0 2px 4px rgba(220, 38, 38, 0.15)",
                            }
                          }}
                        >
                          <Icon>delete_outlined</Icon>
                        </IconButton>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Instruction"
                            multiline
                            rows={2}
                            value={step.instruction}
                            onChange={(e) =>
                              handleChange(
                                "preparation_steps",
                                formData.preparation_steps?.map((ps, i) =>
                                  i === index ? { ...ps, instruction: e.target.value } : ps
                                )
                              )
                            }
                            required
                            error={!!errors[`instruction_${index}`]}
                            helperText={errors[`instruction_${index}`]}
                            InputProps={{
                              startAdornment: (
                                <Icon sx={{ mr: 1, mt: 1, color: THEME_COLORS.text.secondary }}>
                                  description
                                </Icon>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Preparation Time"
                            type="number"
                            value={step.preparation_time}
                            onChange={(e) =>
                              handleChange(
                                "preparation_steps",
                                formData.preparation_steps?.map((ps, i) =>
                                  i === index
                                    ? { ...ps, preparation_time: parseInt(e.target.value) }
                                    : ps
                                )
                              )
                            }
                            InputProps={{
                              startAdornment: (
                                <Icon sx={{ mr: 1, color: THEME_COLORS.text.secondary }}>timer</Icon>
                              ),
                              endAdornment: (
                                <Select
                                  value={step.preparation_time_unit}
                                  onChange={(e) =>
                                    handleChange(
                                      "preparation_steps",
                                      formData.preparation_steps?.map((ps, i) =>
                                        i === index
                                          ? { ...ps, preparation_time_unit: e.target.value }
                                          : ps
                                      )
                                    )
                                  }
                                  variant="standard"
                                  sx={{ ml: 1 }}
                                >
                                  <MenuItem value="min">min</MenuItem>
                                  <MenuItem value="hr">hr</MenuItem>
                                </Select>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Cooking Time"
                            type="number"
                            value={step.cooking_time}
                            onChange={(e) =>
                              handleChange(
                                "preparation_steps",
                                formData.preparation_steps?.map((ps, i) =>
                                  i === index
                                    ? { ...ps, cooking_time: parseInt(e.target.value) }
                                    : ps
                                )
                              )
                            }
                            InputProps={{
                              startAdornment: (
                                <Icon sx={{ mr: 1, color: THEME_COLORS.text.secondary }}>microwave</Icon>
                              ),
                              endAdornment: (
                                <Select
                                  value={step.cooking_time_unit}
                                  onChange={(e) =>
                                    handleChange(
                                      "preparation_steps",
                                      formData.preparation_steps?.map((ps, i) =>
                                        i === index
                                          ? { ...ps, cooking_time_unit: e.target.value }
                                          : ps
                                      )
                                    )
                                  }
                                  variant="standard"
                                  sx={{ ml: 1 }}
                                >
                                  <MenuItem value="min">min</MenuItem>
                                  <MenuItem value="hr">hr</MenuItem>
                                </Select>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Temperature"
                            type="number"
                            value={step.temperature}
                            onChange={(e) =>
                              handleChange(
                                "preparation_steps",
                                formData.preparation_steps?.map((ps, i) =>
                                  i === index
                                    ? { ...ps, temperature: parseInt(e.target.value) }
                                    : ps
                                )
                              )
                            }
                            InputProps={{
                              startAdornment: (
                                <Icon sx={{ mr: 1, color: THEME_COLORS.text.secondary }}>thermostat</Icon>
                              ),
                              endAdornment: (
                                <Select
                                  value={step.temperature_unit}
                                  onChange={(e) =>
                                    handleChange(
                                      "preparation_steps",
                                      formData.preparation_steps?.map((ps, i) =>
                                        i === index
                                          ? { ...ps, temperature_unit: e.target.value }
                                          : ps
                                      )
                                    )
                                  }
                                  variant="standard"
                                  sx={{ ml: 1 }}
                                >
                                  <MenuItem value="C">°C</MenuItem>
                                  <MenuItem value="F">°F</MenuItem>
                                </Select>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  </Box>
                ))}
                {(!formData.preparation_steps || formData.preparation_steps.length === 0) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No preparation steps added yet. Click the "Add Step" button to start adding steps.
                  </Alert>
                )}
              </Box>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );

  const handleOpenAddDialog = (type: "ingredient" | "processed") => {
    setItemDialogType(type);
    setEditingItemIndex(null);
    setItemDialogOpen(true);
  };

  const handleEditItem = (type: "ingredient" | "processed", index: number) => {
    setItemDialogType(type);
    setEditingItemIndex(index);
    setItemDialogOpen(true);
  };

  const renderItemsList = (type: "ingredient" | "processed") => {
    const items = type === "ingredient" ? formData.ingredients : formData.processed_items;

    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon sx={{ color: THEME_COLORS.primary }}>
                  {type === "ingredient" ? "inventory_2" : "category"}
                </Icon>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    color: THEME_COLORS.text.primary,
                    fontFamily: "Montserrat",
                    fontWeight: 600,
                  }}
                >
                  {type === "ingredient" ? "Ingredients" : "Processed Items"}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: alpha(THEME_COLORS.primary, 0.08),
                      color: THEME_COLORS.primary,
                      border: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    {items?.length || 0}
                  </Box>
                </Box>
              </Stack>
              <Button
                startIcon={<Icon>add</Icon>}
                onClick={() => handleOpenAddDialog(type)}
                variant="contained"
                size="small"
                sx={{
                  bgcolor: THEME_COLORS.primary,
                  '&:hover': {
                    bgcolor: THEME_COLORS.primaryHover,
                  },
                }}
              >
                Add {type === "ingredient" ? "Ingredient" : "Item"}
              </Button>
            </Stack>
            <List sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
              {items && items.length > 0 ? (
                items.map((item: any, index: number) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <ListItemText
                      primary={
                        type === "ingredient" ? item.brand_name : item.name
                      }
                      secondary={
                        <Stack direction="row" spacing={1}>
                          <Typography variant="body2">
                            {`${item.quantity || item.consumbtion_quantity} ${
                              item.unit || item.consumbtion_unit
                            }`}
                          </Typography>
                          {type === "ingredient" && item.batch_id && (
                            <Chip
                              size="small"
                              label="Batch Selected"
                              variant="filled"
                              sx={{
                                bgcolor: alpha(THEME_COLORS.success, 0.08),
                                color: THEME_COLORS.success,
                                fontWeight: 500,
                                borderRadius: 1,
                                border: `1px solid ${alpha(THEME_COLORS.success, 0.2)}`,
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <Box>
                          <Tooltip title="Edit Item" arrow placement="top">
                            <IconButton
                              onClick={() => handleEditItem(type, index)}
                              size="small"
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor: alpha(THEME_COLORS.primary, 0.08),
                                color: THEME_COLORS.primary,
                                backdropFilter: "blur(8px)",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                  transform: "translateY(-1px)",
                                  bgcolor: alpha(THEME_COLORS.primary, 0.12),
                                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                                },
                                "&:active": {
                                  transform: "translateY(0)",
                                },
                              }}
                            >
                              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box>
                          <Tooltip title="Remove Item" arrow placement="top">
                            <IconButton
                              onClick={() => {
                                if (type === "ingredient") {
                                  setFormData((prev) => ({
                                    ...prev,
                                    ingredients: prev.ingredients.filter((_, i) => i !== index),
                                  }));
                                } else {
                                  setFormData((prev) => ({
                                    ...prev,
                                    processed_items: (prev.processed_items || []).filter(
                                      (_, i) => i !== index
                                    ),
                                  }));
                                }
                              }}
                              size="small"
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor: alpha(THEME_COLORS.danger, 0.08),
                                color: THEME_COLORS.danger,
                                backdropFilter: "blur(8px)",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                  transform: "translateY(-1px)",
                                  bgcolor: alpha(THEME_COLORS.danger, 0.12),
                                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                                },
                                "&:active": {
                                  transform: "translateY(0)",
                                },
                              }}
                            >
                              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              ) : (
                <Alert severity="info" sx={{ m: 1 }}>
                  No {type === "ingredient" ? "Ingredients" : "Items"} added yet
                </Alert>
              )}
            </List>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <LinearProgress 
          sx={{ 
            backgroundColor: alpha(THEME_COLORS.primary, 0.1),
            '& .MuiLinearProgress-bar': {
              backgroundColor: THEME_COLORS.primary,
            }
          }} 
        />
        <Typography 
          sx={{ 
            mt: 2, 
            textAlign: "center",
            color: THEME_COLORS.text.secondary,
            fontFamily: "Montserrat",
          }}
        >
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <PageHeader
        title={id ? "Edit Bill of Materials" : "Create Bill of Materials"}
        subtitle="Manage your menu items' bill of materials"
        icon="inventory"
        backButton
        onBack={handleCancel}
      />

      <Paper 
        elevation={0}
        sx={{ 
          p: 3,
          border: `1px solid ${alpha(THEME_COLORS.primary, 0.1)}`,
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <form onSubmit={handleSubmit}>
          {renderBasicInfo()}

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Icon sx={{ color: THEME_COLORS.primary }}>inventory_2</Icon>
              Recipe Components
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {renderItemsList("ingredient")}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderItemsList("processed")}
              </Grid>
            </Grid>
          </Box>

          {renderPreparationSteps()}

          <Divider sx={{ my: 4 }} />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleCancel}
              startIcon={<Icon>close</Icon>}
              sx={{
                borderColor: THEME_COLORS.border.main,
                color: THEME_COLORS.primary,
                '&:hover': {
                  borderColor: THEME_COLORS.primary,
                  bgcolor: alpha(THEME_COLORS.primary, 0.04),
                },
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={saving}
              startIcon={<Icon>save</Icon>}
              sx={{
                bgcolor: THEME_COLORS.primary,
                '&:hover': {
                  bgcolor: THEME_COLORS.primaryHover,
                },
              }}
            >
              {id ? "Update" : "Create"} BOM
            </LoadingButton>
          </Stack>
        </form>
      </Paper>

      {itemDialogType === "ingredient" ? (
        <IngredientDialog
          open={itemDialogOpen}
          onClose={() => setItemDialogOpen(false)}
          onSave={(entries) => {
            // The data is now an array of entries
            const selectedIngredient = entries[0];
            const newIngredient: BOMIngredient = {
              item_id: selectedIngredient.ingredient.id,
              item_type: "ingredient",
              quantity: selectedIngredient.quantity,
              unit: selectedIngredient.unit,
              notes: selectedIngredient.notes || "",
              ingredient_id: selectedIngredient.ingredient.ingredient_id,
              batch_id: selectedIngredient.selectedBatch?.batch_id || "",
              brand_name: selectedIngredient.ingredient.name,
            };

            setFormData((prev) => ({
              ...prev,
              ingredients:
                editingItemIndex !== null
                  ? prev.ingredients.map((ing, i) =>
                      i === editingItemIndex ? newIngredient : ing
                    )
                  : [...prev.ingredients, newIngredient],
            }));
            setItemDialogOpen(false);
          }}
          editData={
            editingItemIndex !== null
              ? {
                  ingredient: {
                    ...ingredientOptions.find(
                      (opt) =>
                        opt.ingredient_id ===
                        formData.ingredients[editingItemIndex].ingredient_id
                    )!,
                    type: "ingredient",
                  },
                  quantity: formData.ingredients[editingItemIndex].quantity,
                  unit: formData.ingredients[editingItemIndex].unit,
                  notes: formData.ingredients[editingItemIndex].notes,
                  selectedBatch: formData.ingredients[editingItemIndex].batch_id
                    ? (
                        batchDataCache[
                          formData.ingredients[editingItemIndex].ingredient_id
                        ]?.batches || []
                      ).find(
                        (b) =>
                          b.batch_id ===
                          formData.ingredients[editingItemIndex].batch_id
                      )
                    : undefined,
                }
              : undefined
          }
          ingredientOptions={
            ingredientOptions?.map((opt) => ({
              ...opt,
              type: "ingredient",
            })) || []
          }
          loadingIngredients={loadingIngredients}
          loadBatches={getBatchData}
          mode={editingItemIndex !== null ? "edit" : "add"}
          currentRecipeIngredients={formData.ingredients}
        />
      ) : null}

      {itemDialogType === "processed" ? (
        <ProcessedItemDialog
          open={itemDialogOpen}
          onClose={() => setItemDialogOpen(false)}
          onSave={(entries) => {
            const selectedItem = entries[0];
            if (!selectedItem?.item) return;

            const newProcessedItem = {
              processed_item_id: selectedItem.item._id,
              name: selectedItem.item.name,
              consumbtion_quantity: selectedItem.quantity,
              consumbtion_unit: selectedItem.unit,
            };

            setFormData((prev) => ({
              ...prev,
              processed_items:
                editingItemIndex !== null
                  ? (prev.processed_items || []).map((item, i) =>
                      i === editingItemIndex ? newProcessedItem : item
                    )
                  : [...(prev.processed_items || []), newProcessedItem],
            }));
            setItemDialogOpen(false);
          }}
          processedItems={processedItemOptions.map((item) => ({
            ...item,
            remaining_quantity: item.quantity,
            storage_place: "",
            recipe_name: "",
            cost_per_unit: 0,
          }))}
          loadingItems={loadingProcessedItems}
          mode={editingItemIndex !== null ? "edit" : "add"}
          currentItems={
            formData.processed_items?.map((item) => ({
              processed_item_id: item.processed_item_id,
              quantity: item.consumbtion_quantity,
              unit: item.consumbtion_unit,
            })) || []
          }
          editData={
            editingItemIndex !== null &&
            formData.processed_items?.[editingItemIndex]
              ? {
                  item: processedItemOptions.find(
                    (opt) =>
                      opt._id ===
                      formData.processed_items![editingItemIndex]
                        .processed_item_id
                  )
                    ? {
                        ...processedItemOptions.find(
                          (opt) =>
                            opt._id ===
                            formData.processed_items![editingItemIndex]
                              .processed_item_id
                        )!,
                        remaining_quantity: 0,
                        storage_place: "",
                        recipe_name: "",
                        cost_per_unit: 0,
                      }
                    : null,
                  quantity:
                    formData.processed_items[editingItemIndex]
                      .consumbtion_quantity,
                  unit: formData.processed_items[editingItemIndex]
                    .consumbtion_unit,
                  notes: "",
                }
              : undefined
          }
        />
      ) : null}
    </Box>
  );
};
